import { useEffect, useRef, useState } from "react";
import * as pdfjsLib from "pdfjs-dist/build/pdf";
import { API } from "../lib/api";

pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ""}/pdf.worker.min.js`;

export function usePdf(issue, userKey) {
  const [state, setState] = useState({ doc: null, full: false, ratio: 0.707, loading: false, error: false });
  const id = issue?.id;
  const hasPdf = Boolean(issue?.pdf_pages);
  useEffect(() => {
    if (!id || !hasPdf) return undefined;
    let alive = true;
    let doc = null;
    setState((s) => ({ ...s, loading: true, error: false }));
    (async () => {
      try {
        const t = localStorage.getItem("bpj_token");
        const res = await fetch(`${API}/issues/${id}/pdf`, { headers: t ? { Authorization: `Bearer ${t}` } : {} });
        if (!res.ok) throw new Error("pdf");
        const full = res.headers.get("X-BPJ-Full") === "1";
        doc = await pdfjsLib.getDocument({ data: new Uint8Array(await res.arrayBuffer()) }).promise;
        const p1 = await doc.getPage(1);
        const vp = p1.getViewport({ scale: 1 });
        if (alive) setState({ doc, full, ratio: vp.width / vp.height, loading: false, error: false });
      } catch {
        if (alive) setState((s) => ({ ...s, loading: false, error: true }));
      }
    })();
    return () => { alive = false; if (doc) doc.destroy(); };
  }, [id, hasPdf, userKey]);
  return state;
}

export function PdfCanvas({ doc, n, width, height, textLayer = false }) {
  const ref = useRef(null);
  const layer = useRef(null);
  useEffect(() => {
    if (!doc || !ref.current) return undefined;
    let task = null;
    let tl = null;
    let cancelled = false;
    doc.getPage(n).then((page) => {
      if (cancelled || !ref.current) return;
      const base = page.getViewport({ scale: 1 });
      const css = Math.min(width / base.width, height / base.height);
      const scale = css * Math.min(window.devicePixelRatio || 1, 2);
      const vp = page.getViewport({ scale });
      const c = ref.current;
      c.width = vp.width;
      c.height = vp.height;
      task = page.render({ canvasContext: c.getContext("2d"), viewport: vp });
      task.promise.catch(() => {});
      if (textLayer && layer.current) {
        const el = layer.current;
        el.innerHTML = "";
        const cvp = page.getViewport({ scale: css });
        el.style.width = `${cvp.width}px`;
        el.style.height = `${cvp.height}px`;
        el.style.setProperty("--scale-factor", String(css));
        tl = pdfjsLib.renderTextLayer({ textContentSource: page.streamTextContent(), container: el, viewport: cvp, textDivs: [] });
        tl.promise.catch(() => {});
      }
    });
    return () => { cancelled = true; if (task) task.cancel(); if (tl) tl.cancel(); };
  }, [doc, n, width, height, textLayer]);
  return (
    <div className="relative w-full h-full grid place-items-center bg-white">
      <canvas ref={ref} className="absolute inset-0 w-full h-full object-contain" data-testid={`pdf-page-${n}`} />
      {textLayer && <div ref={layer} className="pdf-text-layer" />}
    </div>
  );
}
